import { Plugin } from "../../Services/PluginInvoker";
import { PluginIDs } from "../../Utilities/PluginIDs";
import { NestedTranslation } from "./init";

/**
 * Deeply merges two translation objects, updating the base with new values.
 *
 * @param baseTranslations - The base translation object.
 * @param updatedTranslations - The new translations to merge.
 * @returns The merged translation object.
 */
export function mergeTranslations(
    baseTranslations: NestedTranslation,
    updatedTranslations: NestedTranslation
): NestedTranslation {
    const mergeObjects = (
        base: NestedTranslation,
        updates: NestedTranslation
    ): NestedTranslation => {
        for (const key in updates) {
            if (typeof updates[key] === 'object' && !Array.isArray(updates[key])) {
                if (typeof base[key] !== 'object' || Array.isArray(base[key]) || base[key] === null) {
                    base[key] = {};
                }
                base[key] = mergeObjects(base[key] as NestedTranslation,
                    updates[key] as NestedTranslation);
            } else {
                base[key] = updates[key];
            }
        }
        return base;
    };

    return mergeObjects({ ...baseTranslations }, updatedTranslations);
}

/**
 * Removes the `_localization:` prefix from the given key.
 *
 * @param keyPath - The key that may include a `_localization:` prefix.
 * @returns The cleaned key without the prefix.
 */
export function getTranslationKey(keyPath: string): string {
    if (!keyPath?.startsWith('_localization:')) return keyPath;
    return keyPath.replace('_localization:', '');
}

/**
 * Converts a localization key to an API message key by replacing underscores
 * and colons with periods.
 *
 * @param keyPath - The localization key.
 * @returns The API message key.
 */
export function getAPIMessageKey(keyPath: string): string {
    const propertyPath = getTranslationKey(keyPath);
    return propertyPath.replace(/[_:]/g, '.');
}

/**
 * Retrieves available locales from the plugin system.
 *
 * @returns A promise that resolves to an array of available locales.
 */
export function getAvailableLocales(): Promise<Array<string>> {
    return Plugin.Invoke(PluginIDs.GetAvailableLocales);
}
